import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { ArrowRight, Clock, Calendar, ChevronRight, BookOpen, MapPin } from 'lucide-react';
import { getRatgeberBySlug, getAllRatgeber } from '../data/ratgeberContent';
import { services, cities } from '../data/seoData';

export default function RatgeberPage() {
  const { slug } = useParams();
  const ratgeber = getRatgeberBySlug(slug);
  
  if (!ratgeber) {
    return <RatgeberNotFound />;
  }

  const { title, metaTitle, metaDescription, heroSubtitle, publishDate, readTime, content } = ratgeber;

  // Structured Data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": metaDescription,
    "datePublished": publishDate,
    "author": {
      "@type": "Organization",
      "name": "FarbWelt Maler Express Service UG (Haftungsbeschränkt)"
    },
    "publisher": {
      "@type": "Organization",
      "name": "FarbWelt Service",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.farbwelt-service.de/logo.png"
      }
    }
  };

  // FAQ Schema
  const faqSchema = content.faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": content.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://www.farbwelt-service.de/ratgeber/${slug}`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#2E2A88] to-[#1a1654] text-white py-16 md:py-20">
          <div className="container mx-auto px-4">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/ratgeber" className="hover:text-white">Ratgeber</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white/90">{title.split(' – ')[0]}</span>
            </nav>

            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-4">
                <span className="bg-[#9B1B30] px-3 py-1 rounded-full text-sm font-medium">
                  <BookOpen className="w-4 h-4 inline mr-1" />
                  Ratgeber
                </span>
                <span className="text-white/70 text-sm flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {readTime} Lesezeit
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-barlow mb-4">
                {title}
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-3xl">
                {heroSubtitle}
              </p>
              
              <div className="mt-6 flex items-center gap-4 text-sm text-white/70">
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(publishDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <article className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Article Content */}
              <div className="lg:col-span-2">
                {/* Intro */}
                <div className="prose prose-lg max-w-none mb-12">
                  {content.intro.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-gray-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Table of Contents */}
                <div className="bg-gray-50 p-6 rounded-xl mb-12">
                  <h2 className="font-bold text-lg mb-4">Inhalt</h2>
                  <ol className="space-y-2">
                    {content.sections.map((section, idx) => (
                      <li key={idx}>
                        <a 
                          href={`#section-${idx}`}
                          className="text-[#9B1B30] hover:underline flex items-center"
                        >
                          <span className="w-6 h-6 bg-[#9B1B30]/10 rounded-full flex items-center justify-center text-sm mr-2">
                            {idx + 1}
                          </span>
                          {section.title}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Content Sections */}
                {content.sections.map((section, idx) => (
                  <section key={idx} id={`section-${idx}`} className="mb-12 scroll-mt-24">
                    <h2 className="text-2xl md:text-3xl font-bold font-barlow mb-6 text-gray-900">
                      {section.title}
                    </h2>
                    <div className="prose prose-lg max-w-none">
                      {section.content.split('\n\n').map((paragraph, pIdx) => (
                        <div key={pIdx} className="mb-4">
                          {paragraph.startsWith('**') ? (
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line"
                               dangerouslySetInnerHTML={{ 
                                 __html: paragraph
                                   .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                   .replace(/\n/g, '<br/>')
                               }} 
                            />
                          ) : paragraph.startsWith('☐') || paragraph.startsWith('-') ? (
                            <div className="text-gray-700 leading-relaxed whitespace-pre-line"
                                 dangerouslySetInnerHTML={{ 
                                   __html: paragraph.replace(/\n/g, '<br/>') 
                                 }} 
                            />
                          ) : (
                            <p className="text-gray-700 leading-relaxed">
                              {paragraph}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}

                {/* FAQ Section */}
                {content.faq && content.faq.length > 0 && (
                  <section className="mt-16">
                    <h2 className="text-2xl md:text-3xl font-bold font-barlow mb-8 text-gray-900">
                      Häufige Fragen
                    </h2>
                    <div className="space-y-6">
                      {content.faq.map((faq, idx) => (
                        <div key={idx} className="bg-gray-50 p-6 rounded-xl">
                          <h3 className="font-bold text-lg mb-2">{faq.question}</h3>
                          <p className="text-gray-600">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-8">
                  {/* CTA Box */}
                  <div className="bg-gradient-to-br from-[#9B1B30] to-[#7a1525] text-white p-6 rounded-xl">
                    <h3 className="font-bold text-lg mb-2">Kostenlose Beratung</h3>
                    <p className="text-white/90 text-sm mb-4">
                      Lassen Sie sich unverbindlich beraten. Wir erstellen Ihnen ein Festpreisangebot.
                    </p>
                    <Link to="/termin" data-testid="sidebar-cta">
                      <Button className="w-full bg-white text-[#9B1B30] hover:bg-gray-100">
                        Termin buchen
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>

                  {/* Related Services */}
                  {content.relatedServices && content.relatedServices.length > 0 && (
                    <div className="bg-gray-50 p-6 rounded-xl">
                      <h3 className="font-bold mb-4">Passende Leistungen</h3>
                      <ul className="space-y-3">
                        {content.relatedServices.map(serviceSlug => {
                          const service = services[serviceSlug];
                          if (!service) return null;
                          return (
                            <li key={serviceSlug}>
                              <Link 
                                to={`/${serviceSlug}-duesseldorf`}
                                className="flex items-center text-[#9B1B30] hover:underline"
                              >
                                <ArrowRight className="w-4 h-4 mr-2" />
                                {service.name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Standorte */}
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h3 className="font-bold mb-4 flex items-center">
                      <MapPin className="w-5 h-5 mr-2 text-[#9B1B30]" />
                      Unsere Standorte
                    </h3>
                    <ul className="space-y-2">
                      {Object.values(cities).map(city => (
                        <li key={city.slug}>
                          <Link 
                            to={`/maler-${city.slug}`}
                            className="text-gray-600 hover:text-[#9B1B30] text-sm"
                          >
                            Maler {city.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <section className="py-16 bg-[#2E2A88] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold font-barlow mb-4">
              Haben Sie Fragen?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Wir beraten Sie kostenlos und unverbindlich. Rufen Sie an oder buchen Sie einen Termin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/termin">
                <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525] text-white px-8">
                  Termin buchen
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:+4921337787688">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2E2A88] px-8">
                  02133 7787688
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Other Ratgeber */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8">Weitere Ratgeber</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {getAllRatgeber().filter(r => r.slug !== slug).map(r => (
                <Link 
                  key={r.slug}
                  to={`/ratgeber/${r.slug}`}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="text-sm text-[#9B1B30] font-medium">{r.category}</span>
                  <h3 className="font-bold mt-2 mb-2">{r.title.split(' – ')[0]}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{r.heroSubtitle}</p>
                  <span className="text-[#9B1B30] text-sm mt-4 inline-flex items-center">
                    Weiterlesen <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

// Ratgeber Listing Page
export function RatgeberListPage() {
  const allRatgeber = getAllRatgeber();

  return (
    <>
      <Helmet>
        <title>Ratgeber | Malerarbeiten Tipps & Kosten | FarbWelt Service</title>
        <meta name="description" content="Expertenwissen rund um Malerarbeiten: Kosten, Ablauf, Tipps. Von Wohnung streichen bis Fassadenarbeiten – alles was Sie wissen müssen." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2E2A88] to-[#1a1654] text-white py-16">
          <div className="container mx-auto px-4">
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Ratgeber</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold font-barlow mb-4">
              Ratgeber & Tipps
            </h1>
            <p className="text-xl text-white/90 max-w-2xl">
              Expertenwissen rund um Malerarbeiten: Kosten, Ablauf und praktische Tipps vom Profi.
            </p>
          </div>
        </section>

        {/* Ratgeber Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allRatgeber.map(r => (
                <Link 
                  key={r.slug}
                  to={`/ratgeber/${r.slug}`}
                  className="bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-shadow group"
                  data-testid={`ratgeber-card-${r.slug}`}
                >
                  <div className="h-48 bg-gradient-to-br from-[#9B1B30]/10 to-[#2E2A88]/10 flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-[#9B1B30]/40 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-[#9B1B30]/10 text-[#9B1B30] px-2 py-1 rounded text-xs font-medium">
                        {r.category}
                      </span>
                      <span className="text-gray-500 text-xs flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {r.readTime}
                      </span>
                    </div>
                    <h2 className="font-bold text-xl mb-2 group-hover:text-[#9B1B30] transition-colors">
                      {r.title.split(' – ')[0]}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {r.heroSubtitle}
                    </p>
                    <span className="text-[#9B1B30] font-medium inline-flex items-center">
                      Jetzt lesen <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold font-barlow mb-4">
              Noch Fragen?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Wir beraten Sie gerne persönlich. Kostenlos und unverbindlich.
            </p>
            <Link to="/termin">
              <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525] text-white">
                Beratungstermin buchen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

// Not Found Component
function RatgeberNotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">Ratgeber nicht gefunden</h1>
        <p className="text-gray-600 mb-8">Der gesuchte Ratgeber existiert leider nicht.</p>
        <Link to="/ratgeber">
          <Button className="bg-[#9B1B30] hover:bg-[#7a1525]">
            Alle Ratgeber ansehen
          </Button>
        </Link>
      </div>
    </div>
  );
}
